<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    popularBranchOf,
    popularExclusions,
    popularRanking,
    type IPopularEntry,
  } from '@/domain/catalog/popular-showcase';
  import type { ICategory } from '@/interfaces/ICategory';
  import type { IItem } from '@/interfaces/IItem';

  /**
   * The «Lo más pedido» list (owner 2026-11-03): the products the storefront
   * shows as the most ordered, best first, as diners see them in the admin's
   * branch and menu. «Ocultar» opens two choices under its row (only the
   * product, or its whole category, naming the other listed products it takes
   * out too); what the owner keeps out follows, with «Mostrar». Every change
   * saves at once, with «Deshacer».
   */
  const props = withDefaults(
    defineProps<{
      /** The sheet holding the list is open: closing it folds the «Ocultar» choices. */
      open?: boolean;
    }>(),
    { open: true }
  );
  const { data, access, online, actions } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const branch = computed(() =>
    popularBranchOf(data.restaurant.value?.branches, data.branchId.value)
  );
  const entries = computed(() =>
    popularRanking({
      rankedIds: branch.value?.popularItemIds ?? [],
      rows: data.rows.value,
      // The ranking's own branch: it falls back to another branch when the admin's has none.
      branchId: branch.value?.id ?? data.branchId.value,
    })
  );
  const exclusions = computed(() => popularExclusions(data.rows.value));
  const hasExclusions = computed(
    () =>
      exclusions.value.items.length > 0 ||
      exclusions.value.categories.length > 0
  );
  const intro = computed(() =>
    data.activeStoreBranches.value.length > 1 && branch.value
      ? t('menuPage.popular.listIntroBranch', { branch: branch.value.name })
      : t('menuPage.popular.listIntro')
  );

  const canEdit = computed(() => access.value.canManage && online.value);
  const editTitle = computed(() => {
    if (!access.value.canManage) return t('menuPage.popular.needsManage');
    return online.value ? undefined : t('menuPage.errors.offline');
  });

  /** The product whose «Ocultar» choices show under its row. */
  const opened = ref<number | null>(null);
  const busy = ref(false);
  const root = ref<HTMLElement | null>(null);

  watch(
    () => props.open,
    (open) => {
      if (!open) opened.value = null;
    }
  );

  /** The other listed products of the opened product's category: «Toda la categoría» takes them out too. */
  const openedAlso = computed(() => {
    const entry = entries.value.find((e) => e.item.id === opened.value);
    if (!entry) return '';
    return entries.value
      .filter(
        (other) =>
          other.category.id === entry.category.id &&
          other.item.id !== entry.item.id
      )
      .map((other) => other.item.name)
      .join(', ');
  });

  function toggle(entry: IPopularEntry) {
    opened.value = opened.value === entry.item.id ? null : entry.item.id;
  }

  async function run(task: () => Promise<void>) {
    if (busy.value || !canEdit.value) return;
    const hadFocus = !!root.value?.contains(document.activeElement);
    busy.value = true;
    opened.value = null;
    try {
      await task();
    } finally {
      busy.value = false;
    }
    if (hadFocus) await keepFocus();
  }

  /**
   * The button pressed goes away with its choices (and its row moves): the
   * list takes the focus, so the keyboard stays inside the sheet.
   */
  async function keepFocus() {
    await nextTick();
    const active = document.activeElement;
    if (root.value && (!active || active === document.body))
      root.value.focus({ preventScroll: true });
  }

  function hideItem(entry: IPopularEntry) {
    void run(() => actions.setItemPopularHidden(entry.item, true));
  }

  function hideCategory(entry: IPopularEntry) {
    void run(() => actions.setCategoryPopularHidden(entry.category, true));
  }

  function showCategory(category: ICategory) {
    void run(() => actions.setCategoryPopularHidden(category, false));
  }

  function showItem(item: IItem) {
    void run(() => actions.setItemPopularHidden(item, false));
  }
</script>

<template>
  <div ref="root" class="v2-catalog-popular-list" tabindex="-1">
    <p v-if="entries.length" class="list-intro">{{ intro }}</p>

    <ol v-if="entries.length" class="list-rows">
      <li
        v-for="entry in entries"
        :key="entry.item.id"
        class="list-row"
        :class="{ 'is-open': opened === entry.item.id }"
      >
        <div class="row-main">
          <span class="row-rank">{{
            t('menuPage.popular.rank', { n: entry.rank })
          }}</span>
          <span class="row-text">
            <span class="row-name">{{ entry.item.name }}</span>
            <span class="row-detail">{{ entry.category.name }}</span>
          </span>
          <span class="row-action" :title="editTitle">
            <PosUiButton
              color="neutral"
              variant="outline"
              icon="eye-off-outline"
              :disabled="!canEdit || busy"
              :aria-expanded="opened === entry.item.id ? 'true' : 'false'"
              @click="toggle(entry)"
              >{{ t('menuPage.popular.hide') }}</PosUiButton
            >
          </span>
        </div>
        <div v-if="opened === entry.item.id" class="row-choices">
          <PosUiButton
            class="row-choice-button"
            color="neutral"
            variant="outline"
            block
            :disabled="busy"
            @click="hideItem(entry)"
            >{{
              t('menuPage.popular.hideOnly', { name: entry.item.name })
            }}</PosUiButton
          >
          <PosUiButton
            class="row-choice row-choice-button"
            color="neutral"
            variant="outline"
            block
            :disabled="busy"
            @click="hideCategory(entry)"
            >{{
              t('menuPage.popular.hideCategory', {
                name: entry.category.name,
              })
            }}</PosUiButton
          >
          <p v-if="openedAlso" class="row-choice-note">
            {{ t('menuPage.popular.hideCategoryAlso', { names: openedAlso }) }}
          </p>
        </div>
      </li>
    </ol>
    <PosUiEmptyState
      v-else
      class="list-empty"
      icon="trending-up"
      :title="t('menuPage.popular.listEmpty')"
      :description="t('menuPage.popular.listEmptyHint')"
    />

    <template v-if="hasExclusions">
      <h3 class="list-heading">{{ t('menuPage.popular.hiddenTitle') }}</h3>
      <ul class="list-rows">
        <li
          v-for="{ category, itemCount } in exclusions.categories"
          :key="`c${category.id}`"
          class="list-row"
        >
          <div class="row-main">
            <span class="row-text">
              <span class="row-name is-muted">{{ category.name }}</span>
              <span class="row-detail">{{
                tc('menuPage.popular.hiddenCategory', itemCount, {
                  n: itemCount,
                })
              }}</span>
            </span>
            <span class="row-action" :title="editTitle">
              <PosUiButton
                color="neutral"
                variant="outline"
                icon="eye-outline"
                :disabled="!canEdit || busy"
                @click="showCategory(category)"
                >{{ t('menuPage.discounts.show') }}</PosUiButton
              >
            </span>
          </div>
        </li>
        <li
          v-for="row in exclusions.items"
          :key="`i${row.item.id}`"
          class="list-row"
        >
          <div class="row-main">
            <span class="row-text">
              <span class="row-name is-muted">{{ row.item.name }}</span>
              <span v-if="row.category" class="row-detail">{{
                row.category.name
              }}</span>
            </span>
            <span class="row-action" :title="editTitle">
              <PosUiButton
                color="neutral"
                variant="outline"
                icon="eye-outline"
                :disabled="!canEdit || busy"
                @click="showItem(row.item)"
                >{{ t('menuPage.discounts.show') }}</PosUiButton
              >
            </span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-popular-list {
    // A focus target only after a choice (keepFocus), not a control.
    &:focus {
      @apply tw-outline-none;
    }

    .list-intro {
      @apply tw-m-0 tw-mb-3 tw-text-sm tw-leading-5 tw-text-neutral-600;
    }

    .list-empty {
      @apply tw-py-6;
    }

    .list-heading {
      @apply tw-m-0 tw-mb-1 tw-mt-5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }

    .list-rows {
      @apply tw-m-0 tw-list-none tw-p-0;
    }

    .list-row {
      @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-py-1.5;

      .row-main {
        @apply tw-flex tw-min-h-[52px] tw-items-center;
      }

      // A minimum, not a width: «Núm. 8» (ca) is wider than «Nº 8».
      .row-rank {
        @apply tw-mr-2 tw-min-w-[36px] tw-flex-none tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-500;
      }

      .row-text {
        @apply tw-mr-3 tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

        .row-name {
          @apply tw-truncate tw-text-base tw-font-semibold tw-leading-5 tw-text-neutral-900;

          &.is-muted {
            @apply tw-text-neutral-700;
          }
        }

        .row-detail {
          @apply tw-truncate tw-text-sm tw-leading-5 tw-text-neutral-500;
        }
      }

      .row-action {
        @apply tw-flex-none;
      }

      .row-choices {
        @apply tw-pb-2 tw-pl-11 tw-pt-1;

        .row-choice {
          @apply tw-mt-2;
        }

        // Long product and category names wrap inside the button.
        .row-choice-button {
          @apply tw-h-auto tw-min-h-[44px] tw-whitespace-normal tw-py-2.5 tw-text-center tw-leading-5;
        }

        .row-choice-note {
          @apply tw-m-0 tw-mt-1.5 tw-text-sm tw-leading-5 tw-text-neutral-500;
        }
      }
    }
  }
</style>
